import * as React from 'react';
import {Props} from './model';
import {View} from 'react-native';
import {useStyles} from '../../hooks/styles';
import {GradientView} from '../gradientView';
import {useTheme} from '../../theme';
import {DeviceSize, maxDesktopContentFooterWidth, useAlign} from '../../theme/helpers';
import {Copywrite} from './components/copywrite';
import {FooterGroup} from './components/group';
import {images} from '../../../assets/icons';
import {AppleStore, FooterShape, FooterShapeMobile, GooglePlay, HuaweiStore, RuStore} from '../../../assets/images';
import {Link} from '../link';
import {Image} from '../image';
import {Text} from '../text';
import {useIsDesktop} from '../../hooks/useIsDesktop';
import {formatPhone} from '../../pipes';

export default ({content}: Props) => {
  const align = useAlign();
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const styles = useStyles(({mediaOnly, mediaExclude}) => ({
    footerContainer: {
      alignSelf: 'center',
      maxWidth: maxDesktopContentFooterWidth,
      width: '100%',
      position: 'relative',
      ...mediaOnly(DeviceSize.desktop, {paddingHorizontal: 24}),
    },
    containerTop: {
      ...mediaOnly(
        DeviceSize.desktop,
        {
          flexDirection: 'row',
          flexWrap: 'wrap',
          paddingBottom: 32,
          paddingTop: 40,
        },
        {
          paddingHorizontal: 30,
          paddingTop: 26,
        },
      ),
    },
    containerBottom: {
      paddingTop: 13,
      paddingBottom: 16,
      borderTopWidth: 1,
      borderTopColor: theme.footer.bottomLineColor,
      borderStyle: 'solid',
      alignItems: 'center',
      ...mediaExclude(DeviceSize.desktop, {paddingHorizontal: 30}),
    },
    copywriteText: {
      textAlign: 'center',
    },
    groupText: {
      ...theme.fonts.body,
      color: theme.footer.textColor,
    },
    contactsTitle: {
      ...mediaOnly(DeviceSize.desktop, theme.fonts.h4, theme.fonts.h5),
      color: theme.footer.textColor,
    },
    otherTitle: {
      ...mediaOnly(DeviceSize.desktop, theme.fonts.h5, theme.fonts.h6),
      color: theme.footer.textColor,
    },
    externalESK: {
      flexDirection: 'row',
    },
    externalESKSvg: {
      alignSelf: 'center',
      marginRight: 15,
    },
    group: {
      ...mediaOnly(DeviceSize.desktop, {flexBasis: '25%', paddingRight: 16}, {marginBottom: 20}),
    },
    groupHotline: mediaOnly(DeviceSize.desktop, {
      flexBasis: '22%',
    }),
    groupUnionNumber: mediaOnly(DeviceSize.desktop, {
      flexBasis: '27.5%',
      paddingRight: 80,
    }),
    groupSocials: mediaOnly(DeviceSize.desktop, {
      flexBasis: '23%',
    }),
    groupMarkets: {
      ...mediaExclude(DeviceSize.desktop, {marginBottom: 10}),
    },
    groupLast: {
      ...mediaOnly(DeviceSize.desktop, {marginTop: -3}, {marginBottom: 18}),
    },
    groupOtherTitle: {
      marginBottom: isDesktop ? 28 : 10,
    },
    socials: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    markets: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      maxWidth: 288,
      justifyContent: 'space-between',
    },
    market: {
      marginBottom: 10,
    },
    social: {
      marginRight: 23,
      color: theme.footer.textColor,
    },
    socialIcon: {
      color: theme.footer.textColor,
    } as any,
    shapeImage: {
      position: 'absolute',
      bottom: 0,
      zIndex: -1,
      ...mediaOnly(DeviceSize.desktop, {left: -120}, {left: 0}),
    },
    socialNumberDescription: {
      maxWidth: 245,
    },
  }));

  const marketIconSize: {width: number; height: number} = isDesktop
    ? {width: 139, height: 41}
    : {width: 130, height: 39};

  const dataFooter = {
    dataSet: {
      class: 'footer',
    },
  };

  const shapeSize: {width: number; height: number} = isDesktop ? {width: 538, height: 211} : {width: 360, height: 95};

  return React.useMemo(() => {
    return (
      <GradientView gradient={{angle: 90, colors: theme.footer.bgGradient}}>
        <View style={styles.footerContainer} {...dataFooter}>
          <View style={styles.containerTop}>
            <FooterGroup
              style={[styles.group, styles.groupHotline]}
              title={
                <Link
                  testID='phone-eskso-contact-support-link-id'
                  dataSetClass='footer-link'
                  href={`tel:${content.esksoContacts.supportPhone}`}
                  external>
                  <Text style={styles.contactsTitle}>{formatPhone(content.esksoContacts.supportPhone)}</Text>
                </Link>
              }>
              <Text testID='hotline-text-id' style={styles.groupText}>
                {content.footer.telephones.hotlineText}
              </Text>
            </FooterGroup>
            <FooterGroup
              style={[styles.group, styles.groupUnionNumber]}
              title={
                <Link
                  testID='phone-eskso-contact-social-link-id'
                  dataSetClass='footer-link'
                  href={`tel:${content.esksoContacts.socialPhone}`}
                  external>
                  <Text testID='phone-eskso-contact-social-link-text-id' style={styles.contactsTitle}>
                    {formatPhone(content.esksoContacts.socialPhone)}
                  </Text>
                </Link>
              }>
              <Text testID='union-number-id' style={[styles.groupText, styles.socialNumberDescription]}>
                {content.footer.telephones.unionNumberText}
              </Text>
            </FooterGroup>
            <FooterGroup
              style={[styles.group, styles.groupSocials]}
              titleStyle={styles.groupOtherTitle}
              childStyle={styles.socials}
              title={
                <Text testID='we-in-social-meida-id' style={styles.otherTitle}>
                  Мы в соцсетях
                </Text>
              }>
              {/* content.footer.socials.facebook ? (
                <Link
                  testID='social-network-fb-app-link-id'
                  accessibilityLabel='facebook'
                  dataSetClass='link-social'
                  href={content.footer.socials.facebook}
                  external={true}
                  style={styles.social}>
                  <images.SocialFacebook width={32} height={32} color={styles.socialIcon.color} />
                </Link>
              ) : null}
              {content.footer.socials.instagram ? (
                <Link
                  testID='social-network-inst-app-link-id'
                  accessibilityLabel='instagram'
                  dataSetClass='link-social'
                  href={content.footer.socials.instagram}
                  external={true}
                  style={styles.social}>
                  <images.SocialInstagram width={29} height={29} color={styles.socialIcon.color} />
                </Link>
              ) : null */}
              {content.footer.socials.vk ? (
                <Link
                  testID='social-network-vk-app-link-id'
                  accessibilityLabel='вконтакте'
                  dataSetClass='link-social'
                  href={content.footer.socials.vk}
                  external={true}
                  style={styles.social}>
                  <images.SocialVk width={42} height={24} color={styles.socialIcon.color} />
                </Link>
              ) : null}
              {content.footer.socials.ok ? (
                <Link
                  testID='social-network-ok-app-link-id'
                  accessibilityLabel='одноклассники'
                  dataSetClass='link-social'
                  href={content.footer.socials.ok}
                  external={true}
                  style={styles.social}>
                  <images.SocialOk width={14} height={24} color={styles.socialIcon.color} />
                </Link>
              ) : null}
            </FooterGroup>
            <FooterGroup
              style={[styles.group, styles.groupMarkets]}
              titleStyle={styles.groupOtherTitle}
              childStyle={styles.markets}
              title={
                <Text testID='official-app-id' style={styles.otherTitle}>
                  Официальное приложение
                </Text>
              }>
              {content.footer.stores.appStore ? (
                <Link
                  testID='ios-app-link-id'
                  accessibilityLabel='ios приложение'
                  dataSetClass='footer-link'
                  href={content.footer.stores.appStore}
                  external
                  style={styles.market}>
                  <Image
                    source={AppleStore as any}
                    width={align(marketIconSize.width)}
                    height={align(marketIconSize.height)}
                  />
                </Link>
              ) : null}
              {content.footer.stores.googlePlay ? (
                <Link
                  testID='android-app-link-id'
                  accessibilityLabel='android приложение'
                  dataSetClass='footer-link'
                  href={content.footer.stores.googlePlay}
                  external
                  style={styles.market}>
                  <Image
                    source={GooglePlay as any}
                    width={align(marketIconSize.width)}
                    height={align(marketIconSize.height)}
                  />
                </Link>
              ) : null}
              {content.footer.stores.appGallery ? (
                <Link
                  testID='huawei-app-link-id'
                  accessibilityLabel='huawei приложение'
                  dataSetClass='footer-link'
                  href={content.footer.stores.appGallery}
                  external
                  style={styles.market}>
                  <Image
                    source={HuaweiStore as any}
                    width={align(marketIconSize.width)}
                    height={align(marketIconSize.height)}
                  />
                </Link>
              ) : null}
              {content.footer.stores.ruStore ? (
                <Link
                  testID='ru-store-app-link-id'
                  accessibilityLabel='RuStore приложение'
                  dataSetClass='footer-link'
                  href={content.footer.stores.ruStore}
                  external
                  style={styles.market}>
                  <Image
                    source={RuStore as any}
                    width={align(marketIconSize.width)}
                    height={align(marketIconSize.height)}
                  />
                </Link>
              ) : null}
            </FooterGroup>
            <FooterGroup style={[styles.group, styles.groupLast, styles.groupHotline]} title={null}>
              <View style={[styles.groupText]}>
                <Link
                  testID='external-website-link-id'
                  dataSetClass='footer-link'
                  style={styles.externalESK}
                  external={true}
                  href={content.esksoContacts.website}>
                  <View style={styles.externalESKSvg}>
                    <images.Up color={styles.groupText.color} width={12} height={12} />
                  </View>
                  <Text testID='esk-portal-id' style={styles.groupText}>
                    Портал ЕСК
                  </Text>
                </Link>
              </View>
            </FooterGroup>
            <FooterGroup style={[styles.group, styles.groupLast, styles.groupUnionNumber]} title={null}>
              <View style={[styles.groupText]}>
                <Link testID='politics-link-id' dataSetClass='footer-link' external={true} href={content.docs.politics}>
                  <Text style={styles.groupText}>Политика конфиденциальности</Text>
                </Link>
              </View>
            </FooterGroup>
          </View>
          <View style={styles.containerBottom}>
            <Copywrite textStyle={styles.copywriteText} />
          </View>
          <View style={styles.shapeImage}>
            <Image
              source={(isDesktop ? FooterShape : FooterShapeMobile) as any}
              width={shapeSize.width}
              height={shapeSize.height}
              style={{width: shapeSize.width, height: shapeSize.height}}
            />
          </View>
        </View>
      </GradientView>
    );
  }, [isDesktop, theme]);
};
